import React, { useCallback } from 'react';
import { useObservable, useCallbackObservable } from 'react-use-observable';

import { App, Modal, Button, Spin } from 'antd';

import { Field, Formik, Form } from 'formik';
import type { FormikProps } from 'formik';
import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ListRadioButtonFormField } from 'v2/components/Shared/FormFields/ListRadioButton';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import { EReproveMotiveId } from 'v2/interfaces/requestManagement/refund';
import refundService from 'v2/services/requestManagement/refund';

import ReproveMotiveSchema from './validations';

interface IValues {
  reprove_motive: string;
  justification: string;
}

export default function ReproveMotive() {
  const { message } = App.useApp();
  const [open] = useObservable(() => refundService.getReproveMotive(), []);
  const [currentRefund] = useObservable(() => refundService.getCurrentRefund(), []);
  const [listReproveMotive] = useObservable(() => refundService.listReproveMotive(), []);

  const closeDetails = useCallback(() => {
    refundService.closeReproveMotive();
  }, []);

  const [onSubmit] = useCallbackObservable(
    ({ reprove_motive, justification }: IValues, { setSubmitting, resetForm }: FormikProps<IValues>) => {
      setSubmitting(true);
      return refundService.reprove(currentRefund, reprove_motive, justification).pipe(
        tap(() => {
          setSubmitting(false);
          resetForm();
          refundService.closeReproveMotive();
          message.success('Solicitação reprovada com sucesso!');
        }),
        catchError(err => {
          setSubmitting(false);
          message.error(typeof err === 'string' ? err : 'Não foi possível reprovar a solicitação.');
          return of(true);
        })
      );
    },
    [currentRefund, message]
  );

  return (
    <Modal open={open} width={600} title='Motivo da Reprova' onCancel={closeDetails} footer={null} closable>
      <Formik
        initialValues={{
          reprove_motive: '',
          justification: ''
        }}
        onSubmit={onSubmit}
        validationSchema={ReproveMotiveSchema}
      >
        {({ isSubmitting, isValid, dirty, values }) => {
          const shouldDisableBtn = isSubmitting || !isValid || !dirty;
          const otherReasonSelected = values.reprove_motive === EReproveMotiveId.OTHER_REASON;

          const checkBoxFormat = listReproveMotive.map(item => ({
            label: item.description,
            value: item.id
          }));

          return (
            <Form className='flex flex-col gap-4'>
              {open && !listReproveMotive ? (
                <div className='flex items-center justify-center p-4'>
                  <Spin />
                </div>
              ) : (
                <div className='flex flex-col gap-4'>
                  <Field
                    id='reprove_motive'
                    name='reprove_motive'
                    options={checkBoxFormat}
                    component={ListRadioButtonFormField}
                  />
                  {otherReasonSelected && (
                    <Field
                      id='justification'
                      name='justification'
                      component={TextFormField}
                      placeholder='Descreva o motivo da reprova do pedido de reembolso. (Lembre-se de que essa informação será direcionada ao cliente.)'
                      multiline
                    />
                  )}
                </div>
              )}
              <div className='flex flex-wrap items-center justify-end gap-2'>
                <Button type='text' onClick={closeDetails} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button loading={isSubmitting} type='primary' htmlType='submit' danger disabled={shouldDisableBtn}>
                  Reprovar Reembolso
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}
